import Cookies from 'js-cookie';
import localforage from 'localforage';
import { Utils } from '@client/utility';

const db = localforage;

const POSITION_PREFIX = 'Positions_';
const CURRENT_INDEX_PREFIX = 'MessageCurrent_';

export const saveImageToDB = async (imageBlob: Blob): Promise<void> => {
  await db.setItem('bgImage', imageBlob);
};

export const getImageFromDB = async (): Promise<Blob | null> => {
  try {
    const result = await db.getItem<Blob>('bgImage');
    return result ?? null;
  } catch {
    return null;
  }
};

export const insertData = async <T>(
  storeName: string,
  data: T,
  key: IDBValidKey
): Promise<void> => {
  await db.setItem(`${storeName}_${key}`, data);
};

export const createTable = async (tableName: string): Promise<void> => {
  console.log(`Table "${tableName}" ready (using key prefix).`);
};

export const saveMessagePosition = async (
  chatUuid: string,
  messageId: number,
  position: number
): Promise<void> => {
  try {
    const key = `${POSITION_PREFIX}${chatUuid}_${messageId}`;
    await db.setItem(key, position);
  } catch (err) {
    console.error('Failed to save message position:', err);
  }
};

export const getMessagePosition = async (
  chatUuid: string,
  messageId: number
): Promise<number | null> => {
  try {
    const key = `${POSITION_PREFIX}${chatUuid}_${messageId}`;
    const position = await db.getItem<number>(key);
    return position ?? null;
  } catch (err) {
    console.error('Failed to get message position:', err);
    return null;
  }
};

export const removeMessagePosition = async (
  chatUuid: string,
  messageId: number
): Promise<void> => {
  try {
    const key = `${POSITION_PREFIX}${chatUuid}_${messageId}`;
    await db.removeItem(key);
  } catch (err) {
    console.error('Failed to remove message position:', err);
  }
};

export const getAllMessagePositions = async (
  chatUuid: string
): Promise<Record<number, number>> => {
  const positions: Record<number, number> = {};
  try {
    await db.iterate((value, key) => {
      if (
        typeof key === 'string' &&
        key.startsWith(`${POSITION_PREFIX}${chatUuid}_`)
      ) {
        const messageId = parseInt(
          key.replace(`${POSITION_PREFIX}${chatUuid}_`, ''),
          10
        );
        if (!isNaN(messageId) && typeof value === 'number') {
          positions[messageId] = value;
        }
      }
    });
    return positions;
  } catch (err) {
    console.error('Failed to get all message positions:', err);
    return {};
  }
};

export const saveMessageCurrentIndex = async (
  messageId: number,
  currentIndex: number
): Promise<void> => {
  try {
    const key = `${CURRENT_INDEX_PREFIX}${messageId}`;
    await db.setItem(key, currentIndex);
  } catch (err) {
    console.error('Failed to save message current index:', err);
  }
};

export const getMessageCurrentIndex = async (
  messageId: number
): Promise<number | null> => {
  try {
    const key = `${CURRENT_INDEX_PREFIX}${messageId}`;
    const currentIndex = await db.getItem<number>(key);
    return currentIndex ?? null;
  } catch (err) {
    console.error('Failed to get message current index:', err);
    return null;
  }
};

export const removeMessageCurrentIndex = async (
  messageId: number
): Promise<void> => {
  try {
    const key = `${CURRENT_INDEX_PREFIX}${messageId}`;
    await db.removeItem(key);
  } catch (err) {
    console.error('Failed to remove message current index:', err);
  }
};

export const clearCookies = (names: string[] = []): void => {
  if (names.length === 0) {
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      Cookies.remove(name, { path: '/' });
    });
  } else {
    names.forEach((name) => Cookies.remove(name, { path: '/' }));
  }
};

export const clearStorage = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};

export const clearLocalForage = async (): Promise<void> => {
  try {
    await db.clear();
  } catch (err) {
    console.error('Error clearing localForage:', err);
  }
};

export const AuthCleanup = async (): Promise<void> => {
  clearStorage();
  clearCookies(['user_token', 'session_token', 'poftok']);
  await clearLocalForage();
};

export const AuthManager = {
  async cleanup() {
    await AuthCleanup();
  },

  async checkLogin(): Promise<boolean> {
    try {
      const { data, error } = await Utils.db.client.auth.getUser();
      if (error) return false;
      return !!data?.user;
    } catch {
      return false;
    }
  },

  reactiveLogin(callback: (isLoggedIn: boolean) => void) {
    const { data } = Utils.db.client.auth.onAuthStateChange(
      (_event, session) => {
        callback(!!session?.user);
      }
    );
    return () => data.subscription.unsubscribe();
  },
};
